<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="750px">
      <v-skeleton-loader
        v-if="loading"
        type="article, actions"
      ></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !data.id
                  ? $t("wkt.admin.training-category.new-window.title")
                  : $t("wkt.admin.training-category.edit-window.title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form ref="form">
              <v-row>
                <v-col cols="9">
                  <v-text-field
                    v-model="data.name"
                    :label="$t('wkt.admin.training-category.edit-window.name')"
                    autocomplete="off"
                    :rules="[validationRules.required]"
                    maxlength="100"
                    counter="100"
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="3" class="d-flex align-center">
                  <v-checkbox
                    v-model="data.isActive"
                    :label="$t('wkt.admin.training-category.edit-window.is-active')"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="data.id"
            color="#ff0000"
            @click="confirmDelete()"
            :disabled="isReadOnly"
          >
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">{{
              $t("wkt.admin.common.dialog-buttons.delete").toUpperCase()
            }}</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400;"
            :disabled="isReadOnly"
          >
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400;"
          >
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="dlgConfirm"
      v-bind:title="$t('wkt.admin.training-category.edit-window.confirm-dialog.title')"
      v-bind:message="$t('wkt.admin.training-category.edit-window.confirm-dialog.text')"
      v-bind:titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      v-bind:titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      v-on:confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog";
import api from "../../../api/worktime.js";
import gwt from "../../../api/gwt.js";
import { Constants } from "../../common/Constants.js";

export default {
  name: "TrainingCategoryDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog,
  },
  data() {
    return {
      showDialog: false,
      loading: true,
      data: {},
      profile: null,
    };
  },
  computed: {
    sessionModuleTraining() {
      return this.profile?.moduleTraining;
    },
    userHasAnyAccess() {
      return this.sessionModuleTraining !== Constants.DENY_ALL;
    },
    isReadOnly() {
      return this.sessionModuleTraining === Constants.ALLOW_READ_ONLY;
    },
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    open(action, payload) {
      this.profile = gwt.getSessionProfile();

      if (!this.userHasAnyAccess) {
        this.$emit("show-alert", {
          text: this.$t("wkt.admin.common.error.try_again_later"),
          color: "error",
          timeout: 3000,
        });
        return;
      }

      this.showDialog = true;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      if (!payload) {
        this.data = {
          companyId: this.profile.companyId,
          isActive: true,
        };
        this.loading = false;
        return;
      }

      this.loading = true;
      api.getTrainingCategoryById(
        payload,
        (res) => {
          this.data = res;
          this.loading = false;
        },
        (error) => {
          this.$emit("show-alert", {
            text: error,
            color: "error",
            timeout: 3000,
          });
          this.loading = false;
        },
      );
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;

      api.storeTrainingCategory(
        this.data,
        (res) => {
          this.loading = false;
          if (res.rsOK) {
            this.showDialog = false;
            this.$emit("refresh");
          } else {
            this.$emit("show-alert", {
              text: res.message,
              color: "error",
              timeout: 3000,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.data);
    },
    deleteItem() {
      this.loading = true;

      api.getTrainingCategoryUsageCount(
        this.data.id,
        (res) => {
          if (res > 0) {
            this.loading = false;
            this.$emit("show-alert", {
              text: this.$t(
                "wkt.admin.training-category.edit-window.associated_items_error",
                [res],
              ),
              color: "error",
              timeout: 3000,
            });
            return;
          }

          api.deleteTrainingCategory(
            this.data.id,
            (deleteRes) => {
              this.loading = false;
              this.showDialog = false;

              if (!deleteRes.rsOK) {
                this.$emit("show-alert", {
                  text: deleteRes.message,
                  color: "error",
                  timeout: 3000,
                });
                return;
              }

              this.$emit("refresh");
            },
            (error) => {
              this.loading = false;
              this.$emit("show-alert", {
                text: error && error.message ? error.message : error,
                color: "error",
                timeout: 3000,
              });
            },
          );
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
  },
};
</script>
